import React, { useEffect, useState, useRef, useContext } from 'react';
import { Card, StatusTip} from 'tea-component';
import { useHistory, useIntl } from 'umi';
import SegmentTabs from './components/SegmentTab';
import MultipleDropSelect from './components/multiple-drop-select';
import html2pdf from 'html2pdf.js';
import { imgToBase64 } from '@/pages/opinion/utils/image';
import { StudioContext } from '../context';
import { getPresentationFilter, getPresentationReport } from '@home/services/studio';
import Abnormal from '@home/components/Abnormal';
import './index.scss';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { PageLogMes } from '@home/utils/buriedPointCfg';
import RenderImg from './components/RenderImg';
import Loading from '@home/components/Loading';

export default function Presentation () {
  const { currentGame, menuPermission } = useContext(StudioContext);
  const {studio_code: studioCode, unified_edition_id: gameId } = currentGame || {};
  const [images, setImages] = useState<Array<{ link_url: string; url: string; }>>([]);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const [reportInsightConfig, setReportInsightConfig] = useState<any[]>([]);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState(false);
  const [reportId, setReportId] = useState("");
  const reportRef = useRef<HTMLDivElement | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { formatMessage } = useIntl();

  useEffect(() => {
    // 设置全局样式
    const element = document.querySelector('.dbh-studio-context');
    if (element && menuPermission === true) {
      (element as HTMLElement).style.backgroundColor = '#ebecf1';
    }
    return () => {
      // 去掉全局样式
      if (element) {
        (element as HTMLElement).style.backgroundColor = '#fff';
      }
    };
  }, [menuPermission]);

  const history = useHistory();
  const { max = 8 } = history?.location?.query || {};

  useEffect(() => {
    getFilter();
    buriedPointGame({gameName: currentGame?.game_name, ...PageLogMes.studioPresentation}, gameId, 'unified_id');
  }, [currentGame]);

  // 获取筛选器
  async function getFilter() {
    try {
      setFilterLoading(true);
      const insightFilter = await getPresentationFilter({
        studio_id: studioCode,
        type: "2",
        game_id: gameId,
        request_type: ["date"]
      });
      setFilterLoading(false);
      setReportInsightConfig(insightFilter?.date || []);
      setFilterError(false);
    } catch {
      setFilterLoading(false);
      setFilterError(true);
    }
  }

  // 获取Presentation报告
  const getReport = async (report: any) => {
    const { id } = report;
    try {
      setReportLoading(true);
      const params = {
        id, 
        game_id: gameId,
        studio_id: studioCode,
        type: "2",
      };
      const { resource_list: reportData } = await getPresentationReport(params);
      const { file_name: fileUrlJson, file_type: fileType } = reportData?.[0];
      const fileUrl = JSON.parse(fileUrlJson);
      setFileType(fileType);
  
      if (fileType === "img") {
        let images = [];
        if (fileUrl.length > 0) {
          images = fileUrl;
        }
        images = images?.map((item: any) => {
          const isUrl = typeof item === 'string';
          const url = isUrl ? item : item?.url;
          const linkUrl = isUrl ? '' : item?.link_url;
          return {
            url,
            link_url: linkUrl?.startsWith?.('/') ? location.origin + linkUrl : linkUrl,
          };
        });
  
        Promise.all(
          images.map((item: { url: string; }) =>
            imgToBase64(item.url),
          ),
        )
          .then((data) => {
            const newImages = images.map((item: { link_url: any; }, index: number) => ({
              link_url: item.link_url,
              url: data[index] as string,
            }));
            setImages(newImages);
          })
          .finally(() => {
            setReportLoading(false);
          });
      } else if (fileType === "pdf") {
        setPdfUrl(fileUrl?.[0]?.url || '');
        setReportLoading(false);
      }
    } catch (error) {
      setReportLoading(false);
      setReportError(true);
    }
  };
  

  const handleInit = (report: any) => {
    getReport(report);
  };

  const handleReportChange = async (report: any) => {
    getReport(report);
  };

  // 下载
  const download = (activeId: string) => {
    setDownloadLoading(true); // 设置 loading
    const element = document.querySelector('.dbo-report-insight-card');
  
    html2pdf(element, {
      filename: `${activeId}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      pagebreak: {
        mode: ['css'],
      },
      enableLinks: true,
      jsPDF: {
        unit: 'px',
        hotfixes: ['px_scaling'],
        format: [element?.scrollWidth, (element?.scrollHeight || 0) + 20],
      },
    }).then(() => {
      setDownloadLoading(false); // 生成 PDF 完成，隐藏 loading
    }).catch((error: any) => {
      console.error('Error generating PDF:', error);
      setDownloadLoading(false); // 出错时也隐藏 loading
    });
  };

  return (
    <>
      {!filterLoading &&
        <Abnormal
          isLoading={false}
          isNoData={reportInsightConfig.length === 0}
          isError={filterError}
          style={{ height: "100%", backgroundColor: "#fff" }}
          component={
            <>
              {reportInsightConfig?.length > 0 && (
                reportInsightConfig.length <= max ? (
                  <SegmentTabs
                    reportInsightConfig={reportInsightConfig}
                    handleReportChange={handleReportChange}
                    handleInit={handleInit}
                    download={download}
                    downloadLoading={downloadLoading}
                    fileType={fileType}
                    setReportId={setReportId}
                  />
                ) : (
                  <MultipleDropSelect
                    reportInsightConfig={reportInsightConfig}
                    handleReportChange={handleReportChange}
                    handleInit={handleInit}
                    setReportId={setReportId}
                  />
                )
              )}
              {reportLoading ? (
                <Loading className="db-studio-presentation-report-loading" />
              ) : (
                <>
                  {reportError ? (
                    <div className="dbo-render-img-status-tip db-studio-presentation-status-tip">
                      <StatusTip
                        hideIcon={!reportLoading}
                        status={'error'}
                        onRetry={() => {
                          getReport({ id: reportId });
                          setReportError(false);
                        }}
                        errorText={formatMessage({ id: 'dbh_presentation_loading_failed' })}
                        retryText={formatMessage({ id: 'dbh_presentation_click_reload' })}
                        loadingText=""
                      />
                    </div>
                  ) : (
                    <>
                      {fileType === "img" && (
                        <Card ref={reportRef} className="dbo-report-insight-card db-studio-presentation-image">
                          {images.map((item: { url: string; link_url: string | null; }) => (
                            item.link_url && /^https?:\/\//.test(item.link_url) ? (
                              <a key={item.url} href={item.link_url} target="_blank" rel="noopener noreferrer">
                                <RenderImg box={true} mode="image" coverImage={item.url} />
                              </a>
                            ) : (
                              <span key={item.url}>
                                <RenderImg box={true} mode="image" coverImage={item.url} />
                              </span>
                            )
                          ))}
                        </Card>
                      )}
                      {fileType === "pdf" && (
                        <div className="dtb-db-iframe-wrap db-studio-presentation-pdf-wrap db-report-pdf">
                          <iframe title="pdf" className="dtb-db-iframe" src={pdfUrl} />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          }
        />
      }
    </>
  );
}